import { Box, Cpu, Database } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ForgeSystemResources, ForgeResourcesUsage } from '../../types';
import { cn } from '../../lib/utils';

interface ResourcesCapacityCardProps {
  resources: ForgeSystemResources;
  usage?: ForgeResourcesUsage;
  className?: string;
}

interface ResourceBarProps {
  label: string;
  icon: React.ElementType;
  capacity: string | number | undefined;
  used?: string | number;
  percentage?: number;
  unit?: string;
}

// Resource bar with progress indicator
const ResourceBar: React.FC<ResourceBarProps> = ({
  label,
  icon: Icon,
  capacity,
  used,
  percentage,
  unit = '',
}) => {
  const displayPercentage = percentage ?? 0;
  const cappedPercentage = Math.min(displayPercentage, 100);
  
  // Determine color based on usage percentage
  const getBarColor = () => {
    if (cappedPercentage >= 90) return 'bg-red-500';
    if (cappedPercentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <span className="text-sm font-medium text-foreground">{label}</span>
        </div>
        <div className="text-right">
          {used !== undefined ? (
            <span className="text-sm text-muted-foreground">
              {used}{unit} / {capacity}{unit}
            </span>
          ) : (
            <span className="text-sm font-semibold text-foreground">
              {capacity}{unit}
            </span>
          )}
        </div>
      </div>
      
      {/* Progress bar - only show if we have usage data */}
      {percentage !== undefined && (
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-300', getBarColor())}
            style={{ width: `${cappedPercentage}%` }}
          />
        </div>
      )}
      
      {/* Percentage display */}
      {percentage !== undefined && (
        <div className="flex justify-end">
          <span className={cn(
            'text-xs font-medium',
            cappedPercentage >= 90 ? 'text-red-600 dark:text-red-400' :
            cappedPercentage >= 70 ? 'text-yellow-600 dark:text-yellow-400' :
            'text-green-600 dark:text-green-400'
          )}>
            {displayPercentage.toFixed(1)}% used
          </span>
        </div>
      )}
    </div>
  );
};

export const ResourcesCapacityCard: React.FC<ResourcesCapacityCardProps> = ({
  resources,
  usage,
  className,
}) => {
  const { t } = useTranslation();

  // Extract metrics from usage, handling both direct and nested formats.
  const usageData = (usage?.data as Record<string, unknown> | undefined) || {};
  const metrics = (usage?.metrics as Record<string, unknown> | undefined) ||
    (usageData.metrics as Record<string, unknown> | undefined) ||
    usageData;
  
  // Extract data from resources.data (Cluster capacity)
  const capacityData = resources.data as Record<string, unknown> || {};
  
  // Parse CPU values from data.cpu_total_m (in millicores)
  const cpuTotalM = capacityData.cpu_total_m as number | undefined;
  const cpuTotal = cpuTotalM !== undefined 
    ? `${(cpuTotalM / 1000).toFixed(1)} cores` 
    : resources.total_cpu || resources.allocatable_cpu || 'N/A';
  const cpuUsed = (metrics.cpu_used as string) || undefined;
  const cpuPercentage = (metrics.cpu_percentage as number) || undefined;

  // Parse Memory values from data.memory_total_mib (in MiB)
  const memoryTotalMib = capacityData.memory_total_mib as number | undefined;
  const memoryTotal = memoryTotalMib !== undefined
    ? `${(memoryTotalMib / 1024).toFixed(1)} GiB`
    : resources.total_memory || resources.allocatable_memory || 'N/A';
  const memoryUsed = (metrics.memory_used as string) || undefined;
  const memoryPercentage = (metrics.memory_percentage as number) || undefined;

  // Parse Pods values from data.pods_capacity
  const podsCapacity = capacityData.pods_capacity as number | undefined;
  const podsTotal = podsCapacity !== undefined
    ? podsCapacity
    : resources.total_pods || 'N/A';
  const podsUsed = (metrics.pods_used as number) || undefined;
  const podsPercentage = (metrics.pods_percentage as number) || undefined;

  // Node count
  const nodeCount = resources.node_count || 
    capacityData.node_count as number | undefined;

  return (
    <div className={cn('bg-card border border-border rounded-xl p-6', className)}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">
          {t('forgeDashboard.resources.title', 'Cluster capacity')}
        </h3>
        {nodeCount !== undefined && (
          <span className="text-sm text-muted-foreground">
            {nodeCount} {t('forgeDashboard.resources.nodes', 'nodes')}
          </span>
        )}
      </div>

      <div className="space-y-6">
        {/* CPU */}
        <ResourceBar
          label={t('forgeDashboard.resources.cpu', 'CPU')}
          icon={Cpu}
          capacity={cpuTotal}
          used={cpuUsed}
          percentage={cpuPercentage}
        />

        {/* Memory */}
        <ResourceBar
          label={t('forgeDashboard.resources.memory', 'Memory')}
          icon={Database}
          capacity={memoryTotal}
          used={memoryUsed}
          percentage={memoryPercentage}
        />

        {/* Pods */}
        <ResourceBar
          label={t('forgeDashboard.resources.pods', 'Pods')}
          icon={Box}
          capacity={podsTotal}
          used={podsUsed}
          percentage={podsPercentage}
        />
      </div>

      {/* Status indicator */}
      {resources.status && resources.status !== 'success' && (
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-yellow-600 dark:text-yellow-400">
            {resources.message || t('forgeDashboard.resources.statusWarning', 'Resource data may be incomplete')}
          </p>
        </div>
      )}
    </div>
  );
};

export default ResourcesCapacityCard;
